import { useState, useMemo, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { SearchIcon, ClipboardCheckIcon, MapIcon, CheckCircle2Icon, XCircleIcon, DownloadIcon, UserIcon } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

// ─── Types ────────────────────────────────────────────────────────────────────

interface TechCheckItem {
  item:   string
  status: "ok" | "observado" | null
}

interface TechCheckEntry {
  date: string
  savedAt: string
  driverName: string | null
  truck: TechCheckItem[]
  trailer: TechCheckItem[]
}

interface TripPlanData {
  savedAt?: string
  driverName?: string
  driverId?: string
  departureDate?: string
  restHours?: number
  route?: {
    origen?: string
    destino?: string
    tipo_movil?: string
    paradas?: Record<string, string[]>   // stopNum → selected option names
    descansos?: Record<string, boolean>  // "stopNum:optionName" → true if sleep stop
  } | null
  weatherConditions?: Array<{
    day: number
    date: string
    ratings: Record<string, string>
  }>
}

interface ShipmentReport {
  id: string
  internalId: number
  senderName: string | null
  recipientCityName: string | null
  originDepartureAt: string | null
  clientName: string | null
  techCheckCount: number
  tripPlanDate: string | null
  tripPlanDriverName: string | null
  lastDriverName: string | null
  // Raw JSONB returned by API
  techCheck: TechCheckEntry[] | null
  tripPlan: TripPlanData | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hasObserved(entries: TechCheckEntry[] | null): boolean {
  if (!entries) return false
  return entries.some(e =>
    e.truck?.some(i => i.status === "observado") ||
    e.trailer?.some(i => i.status === "observado")
  )
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return "—"
  return new Date(val).toLocaleDateString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  })
}

function fmtDateTime(val: string | null | undefined): string {
  if (!val) return "—"
  return new Date(val).toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  })
}

// ─── TechCheck Modal ──────────────────────────────────────────────────────────

function TechCheckModal({
  open,
  onClose,
  entries,
  internalId,
}: {
  open: boolean
  onClose: () => void
  entries: TechCheckEntry[]
  internalId: number
}) {
  const [selected, setSelected] = useState(0)
  const entry = entries[selected]

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <ClipboardCheckIcon className="h-4 w-4 text-blue-600" />
            Verificaciones técnicas — Carga <span className="text-primary font-mono">#{internalId}</span>
            <Badge variant="secondary" className="bg-blue-100 text-blue-700 text-xs ml-1">
              {entries.length} entrada{entries.length !== 1 ? "s" : ""}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Entry list */}
          <div className="w-44 shrink-0 border-r overflow-y-auto bg-muted/30">
            {entries.map((e, i) => (
              <button
                key={i}
                onClick={() => setSelected(i)}
                className={`w-full text-left px-3 py-2.5 text-xs border-b transition-colors ${
                  i === selected
                    ? "bg-blue-50 text-blue-700 font-semibold border-l-2 border-l-blue-500"
                    : "hover:bg-muted/50 text-muted-foreground"
                }`}
              >
                <div className="font-medium">{fmtDate(e.date)}</div>
                <div className="text-[10px] opacity-70 mt-0.5 truncate">
                  {e.driverName ?? "Sin nombre"}
                </div>
              </button>
            ))}
          </div>

          {/* Entry detail */}
          {entry && (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* Header info */}
              <div className="grid grid-cols-2 gap-3 text-xs bg-muted/30 rounded-md p-3">
                <div>
                  <span className="text-muted-foreground">Chofer</span>
                  <p className="font-medium mt-0.5">{entry.driverName ?? "—"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Guardado</span>
                  <p className="font-medium mt-0.5">{fmtDateTime(entry.savedAt)}</p>
                </div>
              </div>

              {/* Truck items */}
              {entry.truck?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Camión
                  </p>
                  <div className="space-y-1">
                    {entry.truck.map((item, j) => (
                      <div
                        key={j}
                        className="flex items-start gap-2 text-xs py-1.5 border-b last:border-0"
                      >
                        {item.status === "ok"
                          ? <CheckCircle2Icon className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />
                          : item.status === "observado"
                          ? <XCircleIcon className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                          : <span className="h-3.5 w-3.5 shrink-0 mt-0.5 inline-block rounded-full border border-muted-foreground/30" />
                        }
                        <span className="flex-1">{item.item}</span>
                        {item.status === "observado" && (
                          <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Observado</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trailer items */}
              {entry.trailer?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Trailer
                  </p>
                  <div className="space-y-1">
                    {entry.trailer.map((item, j) => (
                      <div
                        key={j}
                        className="flex items-start gap-2 text-xs py-1.5 border-b last:border-0"
                      >
                        {item.status === "ok"
                          ? <CheckCircle2Icon className="h-3.5 w-3.5 text-green-500 shrink-0 mt-0.5" />
                          : item.status === "observado"
                          ? <XCircleIcon className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                          : <span className="h-3.5 w-3.5 shrink-0 mt-0.5 inline-block rounded-full border border-muted-foreground/30" />
                        }
                        <span className="flex-1">{item.item}</span>
                        {item.status === "observado" && (
                          <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Observado</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── TripPlan Modal ────────────────────────────────────────────────────────────

const WEATHER_LABELS: Record<string, string> = {
  soleado: "☀️ Soleado",
  viento:  "💨 Viento",
  lluvia:  "🌧️ Lluvia",
  granizo: "🌨️ Granizo",
  nieve:   "❄️ Nieve",
  helada:  "🧊 Helada",
}

const WEATHER_RATING_LABELS: Record<string, string> = {
  good:    "Bueno",
  regular: "Regular",
  bad:     "Malo",
}

function TripPlanModal({
  open,
  onClose,
  plan,
  internalId,
  fallbackDriverName,
}: {
  open: boolean
  onClose: () => void
  plan: TripPlanData
  internalId: number
  fallbackDriverName: string | null
}) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapIcon className="h-4 w-4 text-indigo-600" />
            Plan de viaje — Carga <span className="text-primary font-mono">#{internalId}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-sm">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-3 bg-muted/30 rounded-md p-3 text-xs">
            <div>
              <span className="text-muted-foreground">Chofer</span>
              <p className="font-medium mt-0.5">
                {plan.driverName ?? fallbackDriverName ?? <span className="text-muted-foreground">—</span>}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">Guardado</span>
              <p className="font-medium mt-0.5">{fmtDateTime(plan.savedAt)}</p>
            </div>
            {plan.departureDate && (
              <div>
                <span className="text-muted-foreground">Salida planificada</span>
                <p className="font-medium mt-0.5">{fmtDateTime(plan.departureDate)}</p>
              </div>
            )}
            {plan.restHours != null && (
              <div>
                <span className="text-muted-foreground">Horas de descanso</span>
                <p className="font-medium mt-0.5">{plan.restHours} hs</p>
              </div>
            )}
          </div>

          {/* Route */}
          {plan.route && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Ruta
              </p>
              <div className="bg-muted/30 rounded-md p-3 text-xs space-y-1.5">
                {plan.route.origen && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-16 shrink-0">Origen</span>
                    <span className="font-medium">{plan.route.origen}</span>
                  </div>
                )}
                {plan.route.destino && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-16 shrink-0">Destino</span>
                    <span className="font-medium">{plan.route.destino}</span>
                  </div>
                )}
                {plan.route.tipo_movil && (
                  <div className="flex gap-2">
                    <span className="text-muted-foreground w-16 shrink-0">Combustible</span>
                    <span className="font-medium uppercase">{plan.route.tipo_movil}</span>
                  </div>
                )}
                {plan.route.paradas && Object.keys(plan.route.paradas).length > 0 && (
                  <div className="mt-2 pt-2 border-t space-y-1">
                    <p className="text-muted-foreground mb-2">Paradas</p>
                    {Object.entries(plan.route.paradas)
                      .sort(([a], [b]) => Number(a) - Number(b))
                      .map(([stopNum, opts]) => {
                        const optList = Array.isArray(opts) ? opts : [String(opts)]
                        // Check if any option of this stop is marked as sleep stop
                        const sleepOption = optList.find(
                          opt => plan.route?.descansos?.[`${stopNum}:${opt}`] === true
                        )
                        return (
                          <div key={stopNum} className={`flex items-start gap-2 pl-1 py-1 rounded ${
                            sleepOption ? "bg-indigo-50" : ""
                          }`}>
                            <span className="text-muted-foreground shrink-0 w-4 text-right">{stopNum}.</span>
                            <span className="flex-1">{optList.join(", ")}</span>
                            {sleepOption && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full shrink-0">
                                🌙 Duerme
                              </span>
                            )}
                          </div>
                        )
                      })
                    }
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Weather */}
          {plan.weatherConditions && plan.weatherConditions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Condiciones climáticas
              </p>
              <div className="space-y-2">
                {plan.weatherConditions.map((w) => (
                  <div key={w.day} className="bg-muted/30 rounded-md p-2.5 text-xs">
                    <p className="font-medium mb-1.5">
                      Día {w.day} — {fmtDate(w.date)}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(w.ratings).map(([cond, rating]) => (
                        <span
                          key={cond}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            rating === "bad"
                              ? "bg-red-100 text-red-700"
                              : rating === "regular"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {WEATHER_LABELS[cond] ?? cond}
                          <span className="opacity-70">· {WEATHER_RATING_LABELS[rating] ?? rating}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25

// ─── CSV export helper ────────────────────────────────────────────────────────

function csvEsc(v: string | null | undefined): string {
  const s = v ?? ""
  return s.includes(",") || s.includes('"') || s.includes("\n")
    ? `"${s.replace(/"/g, '""')}"`
    : s
}

function buildCsv(rows: ShipmentReport[]): string {
  const headers = [
    "# Carga", "Remitente", "Destino", "Fecha salida",
    "Cliente", "Verif. técnicas", "Último chofer", "Plan de viaje (guardado)",
  ]
  const lines = rows.map(r => [
    csvEsc(`#${r.internalId}`),
    csvEsc(r.senderName),
    csvEsc(r.recipientCityName),
    csvEsc(r.originDepartureAt ? fmtDate(r.originDepartureAt) : ""),
    csvEsc(r.clientName),
    csvEsc(String(r.techCheckCount)),
    csvEsc(r.tripPlan?.driverName ?? r.tripPlanDriverName ?? r.lastDriverName ?? ""),
    csvEsc(r.tripPlanDate ? fmtDateTime(r.tripPlanDate) : ""),
  ].join(","))
  return [headers.join(","), ...lines].join("\n")
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement("a")
  a.href     = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CargasInformesPage() {
  const [search,    setSearch]    = useState("")
  const [page,      setPage]      = useState(1)
  const [dateFrom,  setDateFrom]  = useState("")
  const [dateTo,    setDateTo]    = useState("")

  // Modal state
  const [techCheckModal, setTechCheckModal] = useState<ShipmentReport | null>(null)
  const [tripPlanModal,  setTripPlanModal]  = useState<ShipmentReport | null>(null)

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery<ShipmentReport[]>({
    queryKey: ["shipments", "reports"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/reports`)
      return res.json()
    },
  })

  // ── Filter + paginate ──────────────────────────────────────────────────────
  const filtered = useMemo<ShipmentReport[]>(() => {
    if (!data) return []
    const term  = search.toLowerCase().trim()
    const from  = dateFrom ? new Date(dateFrom + "T00:00:00") : null
    const to    = dateTo   ? new Date(dateTo   + "T23:59:59") : null
    return data.filter(r => {
      if (term && !(
        r.senderName?.toLowerCase().includes(term) ||
        r.recipientCityName?.toLowerCase().includes(term) ||
        r.clientName?.toLowerCase().includes(term) ||
        (r.tripPlan?.driverName ?? r.tripPlanDriverName ?? r.lastDriverName ?? "")
          .toLowerCase().includes(term) ||
        String(r.internalId).includes(term)
      )) return false
      if (from || to) {
        const dep = r.originDepartureAt ? new Date(r.originDepartureAt) : null
        if (!dep) return false
        if (from && dep < from) return false
        if (to   && dep > to)   return false
      }
      return true
    })
  }, [data, search, dateFrom, dateTo])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  function handleSearch(value: string) { setSearch(value);   setPage(1) }
  function handleDateFrom(value: string) { setDateFrom(value); setPage(1) }
  function handleDateTo(value: string)   { setDateTo(value);   setPage(1) }

  const handleExport = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10)
    downloadCsv(buildCsv(filtered), `informes-cargas-${today}.csv`)
  }, [filtered])

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="Cargas — Informes" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-4">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative w-64">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar remitente, destino, chofer, #ID..."
              className="pl-8 h-9 text-sm"
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>

          {/* Date from */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Salida desde</span>
            <Input
              type="date"
              className="h-9 text-sm w-36"
              value={dateFrom}
              onChange={e => handleDateFrom(e.target.value)}
            />
          </div>

          {/* Date to */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground whitespace-nowrap">hasta</span>
            <Input
              type="date"
              className="h-9 text-sm w-36"
              value={dateTo}
              onChange={e => handleDateTo(e.target.value)}
            />
          </div>

          {/* Clear filters */}
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); setPage(1) }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Limpiar
            </button>
          )}

          <div className="flex-1" />

          {/* Count */}
          <span className="text-xs text-muted-foreground">
            {isLoading ? "Cargando..." : `${filtered.length} carga${filtered.length !== 1 ? "s" : ""}`}
          </span>

          {/* Export */}
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-1.5 text-xs"
            onClick={handleExport}
            disabled={isLoading || filtered.length === 0}
          >
            <DownloadIcon className="h-3.5 w-3.5" />
            Exportar CSV
          </Button>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground py-24">
            Cargando informes...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground py-24">
            <div className="flex flex-col items-center gap-2">
              <ClipboardCheckIcon className="h-8 w-8 opacity-30" />
              <p>No se encontraron cargas con los filtros aplicados.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20"># Carga</TableHead>
                    <TableHead>Remitente</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Fecha salida</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-center w-32">Verif. técnicas</TableHead>
                    <TableHead>Último chofer</TableHead>
                    <TableHead className="text-center w-24">Plan de viaje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map(row => {
                    const chofer = row.tripPlan?.driverName ?? row.tripPlanDriverName ?? row.lastDriverName
                    return (
                    <TableRow key={row.id}>

                      {/* # Carga */}
                      <TableCell>
                        <span className="font-mono text-primary font-semibold text-sm">
                          #{row.internalId}
                        </span>
                      </TableCell>

                      {/* Remitente */}
                      <TableCell className="max-w-45">
                        <span className="line-clamp-2 text-sm">{row.senderName ?? "—"}</span>
                      </TableCell>

                      {/* Destino */}
                      <TableCell className="max-w-40">
                        <span className="line-clamp-2 text-sm">{row.recipientCityName ?? "—"}</span>
                      </TableCell>

                      {/* Fecha salida */}
                      <TableCell className="text-sm whitespace-nowrap">
                        {fmtDate(row.originDepartureAt)}
                      </TableCell>

                      {/* Cliente */}
                      <TableCell className="max-w-40">
                        <span className="line-clamp-2 text-sm">{row.clientName ?? "—"}</span>
                      </TableCell>

                      {/* Verificaciones técnicas — badge clickeable */}
                      <TableCell className="text-center">
                        {row.techCheck && row.techCheck.length > 0 ? (
                          <button
                            onClick={() => setTechCheckModal(row)}
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-semibold text-xs transition-colors cursor-pointer ${
                              hasObserved(row.techCheck)
                                ? "bg-red-100 text-red-700 hover:bg-red-200"
                                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            }`}
                          >
                            <ClipboardCheckIcon className="h-3 w-3" />
                            {row.techCheckCount}
                          </button>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>

                      {/* Último chofer */}
                      <TableCell>
                        {chofer ? (
                          <span className="flex items-center gap-1.5 text-sm">
                            <UserIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            {chofer}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>

                      {/* Plan de viaje — emoji clickeable */}
                      <TableCell className="text-center">
                        {row.tripPlan ? (
                          <button
                            onClick={() => setTripPlanModal(row)}
                            title={`Plan guardado: ${fmtDateTime(row.tripPlanDate ?? row.tripPlan?.savedAt)}`}
                            className="text-xl hover:scale-110 transition-transform cursor-pointer"
                          >
                            🗺️
                          </button>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>

                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                <span>
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length} resultados
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-muted transition-colors"
                  >
                    ‹
                  </button>
                  <span className="px-2">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-muted transition-colors"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── TechCheck Modal ────────────────────────────────────────────── */}
      {techCheckModal && (
        <TechCheckModal
          open={!!techCheckModal}
          onClose={() => setTechCheckModal(null)}
          entries={techCheckModal.techCheck ?? []}
          internalId={techCheckModal.internalId}
        />
      )}

      {/* ── TripPlan Modal ─────────────────────────────────────────────── */}
      {tripPlanModal?.tripPlan && (
        <TripPlanModal
          open={!!tripPlanModal}
          onClose={() => setTripPlanModal(null)}
          plan={tripPlanModal.tripPlan}
          internalId={tripPlanModal.internalId}
          fallbackDriverName={tripPlanModal.tripPlanDriverName ?? tripPlanModal.lastDriverName}
        />
      )}
    </>
  )
}
